import { useMemo } from "react"
import { useRegistrationAccesses } from "../registration/access"
import {
  getCoverPageContinueState,
  type CoverPageContinueOptions,
  type CoverPageContinueState
} from "./coverPageContinue"

export type CoverPageContinueValue = CoverPageContinueState & {
  /**
   * False until the access map has loaded successfully. Callers must not treat
   * unknown access as confirmed empty (keep the default Offer / Join CTA).
   */
  ready: boolean
  loading: boolean
  error: Error | undefined
  /** Re-run the RegistrationAccess query (e.g. after a failed lookup). */
  refetch: () => Promise<unknown>
}

/**
 * Client-derived Continue state for a Cover Page resource from the session
 * RegistrationAccess cache — no Offer schema changes.
 */
export function useCoverPageContinue(
  resourceId: number | null | undefined,
  {
    canCreateAnother = false,
    scopeToCohort = false,
    cohortId,
    scopeToResourceLicenseOffering = false,
    resourceLicenseOfferingId
  }: CoverPageContinueOptions = {}
): CoverPageContinueValue {
  const { index, ready, loading, error, refetch } = useRegistrationAccesses()

  const state = useMemo(
    () =>
      getCoverPageContinueState(index, resourceId, {
        canCreateAnother,
        scopeToCohort,
        cohortId,
        scopeToResourceLicenseOffering,
        resourceLicenseOfferingId
      }),
    [
      index,
      resourceId,
      canCreateAnother,
      scopeToCohort,
      cohortId,
      scopeToResourceLicenseOffering,
      resourceLicenseOfferingId
    ]
  )

  // Until ready, force mode "none" so we keep the create/join CTA rather than
  // flashing Continue or incorrectly assuming no access. Errors stay unready —
  // never treat a failed lookup as confirmed empty access.
  if (!ready || error) {
    return {
      accesses: [],
      mode: "none",
      canCreateAnother: false,
      singleMetaVerb: null,
      ready: false,
      loading,
      error,
      refetch
    }
  }

  return {
    ...state,
    ready: true,
    loading,
    error: undefined,
    refetch
  }
}
